


























































































































import { Component, Ref, Vue, Watch } from "vue-property-decorator";
import BaseDialogComponent from "@/components/common/BaseDialog.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import {
  MiraComparisonCandidate,
  MiraComparisonKindOption,
  MiraSavedComparison,
  MiraSavedComparisonKind,
} from "@/data/models/MiraSavedComparison";
import { getClientId } from "@/data/repository/ClientRepository";
import { getReactiveActiveUser, persistActiveUserMiraComparisons } from "@/data/repository/UserRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import {
  COLOR_ALL_DEVICES_EDIT_ICON,
  COLOR_DARK_FILL_TEXT,
  COLOR_MIRA_TILE_SELECTED,
  COLOR_MIRA_TILE_SELECTED_DARK,
  COLOR_WHITE_TEXT,
} from "@/utils/colors";
import { toggleFigureChoice } from "@/utils/MiraOverview";
import {
  MIRA_SAVED_COMPARISONS_MAX,
  MIRA_SAVED_COMPARISON_ENTITIES_MAX,
  removeSavedComparison,
  savedComparisonsFor,
  upsertSavedComparison,
} from "@/utils/MiraSavedComparisons";
import { themed } from "@/utils/Theme";
import { generateRandomUuid } from "@/utils/Utils";

const NAME_MAX_LENGTH = 40;

/**
 * "Vergleiche verwalten" (Hans, #2610137 tasks 22 / 24; Taimoor, 2026-11-02): the user's saved comparisons
 * downwards in this client, at most {@link MIRA_SAVED_COMPARISONS_MAX}, each a name and up to
 * {@link MIRA_SAVED_COMPARISON_ENTITIES_MAX} Standorte or Mandanten picked in the order their bars stand
 * (the 1-2-3 of the landing card settings). Saved on the user's own record, keyed by client.
 *
 * `show()` resolves, once the list closes, with the id of the comparison saved last (so the screen can
 * choose it), or `null` when none was. Read-only (no NEU, no row actions) when `editable` is false: under
 * Sicht auf, or without a user record.
 */
@Component({
  components: {
    "base-dialog": BaseDialogComponent,
    "confirmation-dialog": ConfirmationDialogComponent,
  },
})
export default class MiraComparisonsDialogComponent extends Vue {
  @Ref("listDialog")
  listDialog!: BaseDialogComponent;

  @Ref("editDialog")
  editDialog!: BaseDialogComponent;

  @Ref("confirm")
  confirmDialog!: ConfirmationDialogComponent;

  readonly maxComparisons = MIRA_SAVED_COMPARISONS_MAX;
  readonly maxEntities = MIRA_SAVED_COMPARISON_ENTITIES_MAX;
  readonly nameMaxLength = NAME_MAX_LENGTH;

  busy = false;
  editable = false;
  // Initialised, not `!`: the computeds below must track them from the first render (edit-mode-dialogs.md).
  /** The kinds offered, each with what it can pick. */
  kinds: MiraComparisonKindOption[] = [];
  /** The kind in the editor. */
  kind: MiraSavedComparisonKind | null = null;
  /** The comparison in the editor, or `null` for a new one. */
  editing: MiraSavedComparison | null = null;
  name = "";
  /**
   * Whether the name was typed in since the editor opened: only then does an empty name show its error. Vuetify
   * checks a field once it loses focus, so choosing the kind first flagged the untouched name (2026-11-03);
   * SICHERN stays disabled without a name either way.
   */
  nameEdited = false;
  picked: string[] = [];
  private savedId: string | null = null;

  private resolve: ((result: string | null) => void) | null = null;

  /** The user's saved comparisons of the offered kinds in this client, live from their record. */
  get comparisons(): MiraSavedComparison[] {
    return savedComparisonsFor(getReactiveActiveUser(), getClientId(), this.kinds.map((option) => option.kind));
  }

  /** What the editor's kind can pick. */
  get candidates(): MiraComparisonCandidate[] {
    return this.candidatesOf(this.kind);
  }

  candidatesOf(kind: MiraSavedComparisonKind | null): MiraComparisonCandidate[] {
    return this.kinds.find((option) => option.kind === kind)?.candidates ?? [];
  }

  get kindItems(): Array<{ text: string; value: MiraSavedComparisonKind }> {
    return this.kinds.map((option) => ({ text: this.$t(`mira.comparison.saved.kinds.${option.kind}`).toString(), value: option.kind }));
  }

  /** Another kind starts the picks again: an insurer is no Standort. */
  onKindChange(kind: MiraSavedComparisonKind) {
    this.kind = kind;
    this.picked = [];
  }

  get editIconColor(): string {
    return COLOR_ALL_DEVICES_EDIT_ICON;
  }

  get hasChanged(): boolean {
    const name = this.name;
    const picked = this.picked;
    const before = this.editing;
    const beforeIds = before?.entityIds ?? [];
    return (
      name.trim() !== (before?.name ?? "") ||
      picked.length !== beforeIds.length ||
      picked.some((id, index) => id !== beforeIds[index])
    );
  }

  get canSave(): boolean {
    return this.hasChanged && this.name.trim() !== "" && this.picked.length > 0;
  }

  nameRule(value: string): boolean | string {
    return (value ?? "").trim() !== "" || this.$t("mira.comparison.saved.nameRequired").toString();
  }

  /** A kind-specific text: `mira.comparison.saved.<key>.<kind>`. */
  kindText(key: string, params: unknown[] = []): string {
    return this.kind ? this.$t(`mira.comparison.saved.${key}.${this.kind}`, params).toString() : "";
  }

  /** "Versicherer: Allianz, ERGO", the kind named where the client offers more than one. */
  comparisonSubtitle(comparison: MiraSavedComparison): string {
    const names = this.entityNames(comparison);
    return this.kinds.length > 1 ? `${this.$t(`mira.comparison.saved.kinds.${comparison.kind}`).toString()}: ${names}` : names;
  }

  /** The comparison's entities in their order, named; one no longer there (a deleted branch) is left out. */
  entityNames(comparison: MiraSavedComparison): string {
    const candidates = this.candidatesOf(comparison.kind);
    return comparison.entityIds
      .map((id) => candidates.find((candidate) => candidate.id === id)?.label)
      .filter((label): label is string => !!label)
      .join(", ");
  }

  positionText(id: string): string {
    const index = this.picked.indexOf(id);
    return index >= 0 ? String(index + 1) : "";
  }

  /**
   * The landing card settings' badges: a picked entity filled in the screens' tile-selected blue with its
   * number, one not picked an empty dotted circle in the same blue, the place it could take; none once all
   * {@link MIRA_SAVED_COMPARISON_ENTITIES_MAX} are picked, since nothing more can be added (Taimoor, 2026-11-03).
   */
  positionBadgeStyle(id: string): Record<string, string> {
    const color = themed(COLOR_MIRA_TILE_SELECTED, COLOR_MIRA_TILE_SELECTED_DARK);
    if (!this.picked.includes(id)) {
      return this.picked.length < MIRA_SAVED_COMPARISON_ENTITIES_MAX ? { borderColor: color, borderStyle: "dashed" } : {};
    }
    return { backgroundColor: color, borderColor: color, color: themed(COLOR_WHITE_TEXT, COLOR_DARK_FILL_TEXT) };
  }

  /**
   * @param kinds - what this client compares downwards (Standorte or Mandanten, Versicherer), each with the
   *   entities it can pick, in the order to list them.
   * @param editable - whether the user may create, change and delete comparisons.
   */
  show(kinds: MiraComparisonKindOption[], editable: boolean): Promise<string | null> {
    this.kinds = kinds;
    this.kind = kinds[0]?.kind ?? null;
    this.editable = editable;
    this.savedId = null;

    this.listDialog.show({
      // One list for every kind, so the Versicherer join it beside the Standorte or Mandanten.
      title: this.$t("mira.comparison.saved.listTitle").toString(),
      positiveButtonText: editable ? this.$t("mira.comparison.saved.create").toString() : null,
      negativeButtonText: this.$t("common.close").toString(),
      maxWidth: 600,
      scrollable: true,
      closeOnSave: false,
    });
    return new Promise((resolve) => (this.resolve = resolve));
  }

  onCreate() {
    this.openEditor(null);
  }

  onEdit(comparison: MiraSavedComparison) {
    this.openEditor(comparison);
  }

  private openEditor(comparison: MiraSavedComparison | null) {
    this.editing = comparison;
    // A saved comparison keeps its kind; a new one starts with the first offered.
    this.kind = comparison?.kind ?? this.kinds[0]?.kind ?? null;
    this.name = comparison?.name ?? "";
    this.nameEdited = false;
    this.picked = [...(comparison?.entityIds ?? [])];
    this.editDialog.show({
      title: this.$t(comparison ? "mira.comparison.saved.editTitle" : "mira.comparison.saved.create").toString(),
      positiveButtonText: this.$t("common.save").toString(),
      negativeButtonText: this.$t("common.close").toString(),
      // ZURÜCKSETZEN beside SICHERN: back to the saved state without leaving the editor (Taimoor, 2026-11-02).
      neutralPositiveButtonText: this.$t("mira.comparison.saved.reset").toString(),
      closeOnNeutralPositiveAction: false,
      maxWidth: 600,
      scrollable: true,
      persistent: false,
      closeOnSave: false,
    });
  }

  /** ZURÜCKSETZEN: the name and the picks as saved, or empty for a new comparison. */
  onReset() {
    this.kind = this.editing?.kind ?? this.kinds[0]?.kind ?? null;
    this.name = this.editing?.name ?? "";
    this.nameEdited = false;
    this.picked = [...(this.editing?.entityIds ?? [])];
  }

  /** SCHLIEßEN while nothing changed, ABBRECHEN after; outside clicks close only an unchanged editor. */
  @Watch("hasChanged")
  onHasChanged(hasChanged: boolean) {
    this.editDialog.negativeButtonText = this.$t(hasChanged ? "common.abort" : "common.close").toString();
    this.editDialog.persistent = hasChanged;
  }

  onToggle(id: string) {
    this.picked = toggleFigureChoice(this.picked, id, MIRA_SAVED_COMPARISON_ENTITIES_MAX);
  }

  /** SCHLIEßEN has already closed the editor; ABBRECHEN asks first. */
  async onEditorNegative() {
    if (!this.hasChanged) {
      return;
    }
    const discard = await this.confirmDialog.show({
      title: this.$t("common.confirmationDialog.unsavedChangesTitle").toString(),
      message: this.$t("common.confirmationDialog.unsavedChangesMessage").toString(),
      acceptMessage: this.$t("common.abort").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (discard) {
      this.editDialog.isShowing = false;
    }
  }

  async onSave() {
    const clientId = getClientId();
    if (!this.canSave || !this.kind || !clientId) {
      return;
    }
    const comparison: MiraSavedComparison = {
      id: this.editing?.id ?? generateRandomUuid(),
      name: this.name.trim(),
      kind: this.kind,
      entityIds: [...this.picked],
    };
    // The whole list of the client, every kind, so the other kind's comparisons are kept.
    const all = getReactiveActiveUser()?.miraComparisons?.[clientId] ?? [];
    if (await this.persist(clientId, upsertSavedComparison(all, comparison), "mira.comparison.saved.saved")) {
      this.savedId = comparison.id;
      this.editDialog.isShowing = false;
    }
  }

  async onDelete(comparison: MiraSavedComparison) {
    const clientId = getClientId();
    if (!clientId) {
      return;
    }
    const confirmed = await this.confirmDialog.show({
      title: this.$t("mira.comparison.saved.deleteTitle").toString(),
      message: this.$t("mira.comparison.saved.deleteMessage", [comparison.name]).toString(),
      acceptMessage: this.$t("common.delete").toString(),
      rejectMessage: this.$t("common.back").toString(),
    });
    if (!confirmed) {
      return;
    }
    const all = getReactiveActiveUser()?.miraComparisons?.[clientId] ?? [];
    await this.persist(clientId, removeSavedComparison(all, comparison.id), "mira.comparison.saved.deleted");
    if (this.savedId === comparison.id) {
      this.savedId = null;
    }
  }

  private async persist(clientId: string, list: MiraSavedComparison[], successKey: string): Promise<boolean> {
    this.busy = true;
    try {
      await persistActiveUserMiraComparisons(clientId, list);
      SnackbarModule.showSuccess({ key: successKey });
      return true;
    } catch (error) {
      SnackbarModule.showError({ key: "mira.comparison.saved.saveError" });
      return false;
    } finally {
      this.busy = false;
    }
  }

  finish() {
    this.resolve?.(this.savedId);
    this.resolve = null;
  }
}
